// Full-screen emotional paywall modal. One component, 4 variants.
// Prices auto-adapt to region: India → INR, rest of world → USD.
import { Sparkles, Flame, AlertTriangle, Clock, Star, Users } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useRegion } from "@/hooks/useRegion";
import { cn } from "@/lib/utils";
import type { PaywallVariant } from "@/hooks/usePaywallTriggers";
// Use the landing-page showcase hero so the paywall always feels aspirational,
// even on a user's first session before they have any try-ons of their own.
import paywallHero from "@/assets/showcase/after-a.webp";

interface Props {
  open: boolean;
  variant: PaywallVariant | null;
  onClose: () => void;
  onDismiss: () => void; // user tapped "Maybe later"
  onBuy: (packId: "mini" | "value") => void;
}

interface CopySpec {
  icon: React.ComponentType<{ className?: string }>;
  chip: string;
  chipClass: string;
  title: string;
  sub: string;
  // INR (India)
  priceLineInr: string;
  breakdownInr: string;
  // USD (international)
  priceLineUsd: string;
  breakdownUsd: string;
  priceSub: string;
  cta: string;
  pack: "mini" | "value";
}

const COPY: Record<PaywallVariant, CopySpec> = {
  hype: {
    icon: Flame,
    chip: "Your best look yet",
    chipClass: "bg-primary/15 text-primary",
    title: "This looks AMAZING on you!",
    sub: "You just unlocked your best style 👕 — try 5 more outfits.",
    priceLineInr: "₹49",
    breakdownInr: "₹49 = 5 looks = just ₹9 per outfit",
    priceLineUsd: "$2.99",
    breakdownUsd: "$2.99 = 5 looks = just $0.60 per outfit",
    priceSub: "for 5 more outfits",
    cta: "✨ Continue Styling",
    pack: "mini",
  },
  fomo: {
    icon: AlertTriangle,
    chip: "Don't miss out",
    chipClass: "bg-warning/15 text-warning",
    title: "Don't miss your best look",
    sub: "Your last result was 🔥 — keep the streak going before it slips away.",
    priceLineInr: "₹49",
    breakdownInr: "₹49 = 5 looks = just ₹9 per outfit",
    priceLineUsd: "$2.99",
    breakdownUsd: "$2.99 = 5 looks = just $0.60 per outfit",
    priceSub: "for 5 more outfits",
    cta: "Get More Looks",
    pack: "mini",
  },
  urgency: {
    icon: Clock,
    chip: "Last try remaining",
    chipClass: "bg-warning/15 text-warning",
    title: "⏳ Last try remaining",
    sub: "Don't stop now — you're just getting started. Find your perfect outfit today.",
    priceLineInr: "₹49",
    breakdownInr: "₹49 = 5 looks = just ₹9 per outfit",
    priceLineUsd: "$2.99",
    breakdownUsd: "$2.99 = 5 looks = just $0.60 per outfit",
    priceSub: "for 5 more outfits",
    cta: "Continue Styling",
    pack: "mini",
  },
  upsell249: {
    icon: Star,
    chip: "⭐ Most Popular Choice",
    chipClass: "bg-primary/15 text-primary",
    title: "You're loving your styles 😎",
    sub: "Unlock 38 try-ons and never run out mid-scroll.",
    priceLineInr: "₹249",
    breakdownInr: "₹249 = 38 looks = just ₹6.5 per outfit",
    priceLineUsd: "$9.99",
    breakdownUsd: "$9.99 = 38 looks = just $0.26 per outfit",
    priceSub: "for 38 outfits",
    cta: "Upgrade Now",
    pack: "value",
  },
};

export const PaywallModal = ({ open, variant, onClose, onDismiss, onBuy }: Props) => {
  const region = useRegion();

  if (!variant) return null;
  const copy = COPY[variant];
  const Icon = copy.icon;
  const priceLine = region === "in" ? copy.priceLineInr : copy.priceLineUsd;
  const breakdown = region === "in" ? copy.breakdownInr : copy.breakdownUsd;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onDismiss();
      }}
    >
      <DialogContent
        className="max-w-sm overflow-hidden border-0 p-0 bg-background sm:rounded-2xl"
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Hero image — landing-page showcase look */}
        <div className="relative aspect-[4/5] w-full overflow-hidden bg-muted">
          <img
            src={paywallHero}
            alt="Aspirational outfit look"
            className="h-full w-full object-cover animate-scale-in"
            loading="lazy"
          />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
          {/* Chip */}
          <div
            className={cn(
              "absolute left-4 top-4 flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider backdrop-blur-sm",
              copy.chipClass,
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            {copy.chip}
          </div>
        </div>

        {/* Content */}
        <div className="-mt-6 space-y-3 px-5 pb-5 relative z-10 animate-fade-in">
          <h2 className="text-center text-xl font-bold leading-tight">{copy.title}</h2>
          <p className="text-center text-sm text-muted-foreground">{copy.sub}</p>

          {/* Price block */}
          <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4 text-center">
            <div className="flex items-baseline justify-center gap-2">
              <span className="text-4xl font-black tabular-nums text-primary">
                {priceLine}
              </span>
              <span className="text-sm font-medium text-muted-foreground">
                {copy.priceSub}
              </span>
            </div>
            <p className="mt-1 text-xs font-medium text-foreground/70">{breakdown}</p>
          </div>

          {/* Social proof */}
          <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <Users className="h-3.5 w-3.5" />
            <span>🔥 12,000+ users found their perfect outfit</span>
          </div>

          {/* CTA */}
          <Button
            className="h-13 w-full gradient-primary text-primary-foreground text-base font-bold shadow-elevated hover:opacity-95 animate-scale-in"
            onClick={() => {
              onBuy(copy.pack);
              onClose();
            }}
          >
            <Sparkles className="mr-1.5 h-4 w-4" />
            {copy.cta}
          </Button>

          <button
            type="button"
            onClick={onDismiss}
            className="block w-full text-center text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            Maybe later
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
